import { styled } from "styled-components";
import { tokens } from "./tokens";

/**
 * Scrollable content region for the Tractor State feature. Sits inside the
 * app's <Right> shell (between the shared Navbar and Footer) and carries the
 * AeroVect off-white canvas + font stack the design expects.
 */
export const Page = styled.div`
  flex: 1;
  /* Let this flex child shrink below its content's intrinsic width; without it,
     a wide table forces the whole page wider than the viewport. */
  min-width: 0;
  min-height: 0;
  /* A column, so a view that wants the leftover height can ask for it with
     flex: 1 and get the height that is actually left — after the source banner,
     which is a sibling here and is present in snapshot mode. The alternative,
     height: 100% on the view, silently overflows by the banner's height
     whenever one is shown. Views that don't want it (the detail route, the
     load-failure card) opt out with flex: none, via Container. */
  display: flex;
  flex-direction: column;
  /* Clip the horizontal axis explicitly. Left implicit, overflow-y:auto would
     promote overflow-x to auto (CSS spec), so the page itself grew a horizontal
     scrollbar for the wide table — the table must scroll inside its own box, not
     here, or the scrollbar reflows the controls above it. scrollbar-gutter keeps
     the vertical scrollbar from shifting content when it appears/disappears. */
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  background: ${tokens.offWhite};
  color: ${tokens.ink};
  font-family: ${tokens.fontSans};
  -webkit-font-smoothing: antialiased;

  a {
    color: ${tokens.blue};
    text-decoration: none;
  }
  a:hover {
    color: ${tokens.blueElectric};
    text-decoration: underline;
  }
`;

/**
 * Centred content column.
 *
 * The cap exists to stop text running to unreadable line lengths, not to keep
 * the page small — on the 3440px displays this console is actually used on, a
 * 1200px column was an island with more empty pixels beside it than content in
 * it. The caps are now set per view by what that view *does* with the width
 * (the fleet grid adds cards, the detail adds a column), and the things that
 * must not stretch — prose, a label-to-value row — are capped where they live
 * rather than by starving the whole page.
 */
export const Container = styled.div<{ $maxWidth: number }>`
  max-width: ${(props) => props.$maxWidth}px;
  width: 100%;
  box-sizing: border-box;
  /* Sized by its content inside Page's column — it scrolls with the page rather
     than clipping itself to the viewport. Inert outside a flex container. */
  flex: none;
  margin: 0 auto;
  padding: 36px 40px 64px;

  @media (max-width: 760px) {
    padding: 20px 16px 48px;
  }
`;

export const Card = styled.div`
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 14px;
  box-shadow: ${tokens.shadowSm};
`;

export const H1 = styled.h1`
  margin: 0;
  font-size: 34px;
  font-weight: 300;
  letter-spacing: -0.02em;
`;

export const Subtitle = styled.div`
  font-size: 13.5px;
  color: ${tokens.slate};
  margin-top: 4px;
`;

/** Uppercase wide-tracked eyebrow label used across the design. */
export const Eyebrow = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.slate};
`;
